import { forwardRef, memo, ReactNode, useEffect, useMemo, useState } from 'react';

import { RiArrowDownSLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  Box,
  BoxProps,
  Grid,
  Input,
  Loader,
  MantineSize,
  NumberInput,
  Select,
  ComboboxItem,
  useMantineColorScheme
} from '@mantine/core';

import {
  GroupSelectOverrideClassNames,
  GroupInputOverrideClassNames
} from '../groupstyles';

interface NumberSelectGroupProps extends BoxProps {
  /**
   * enable loading
   */
  loading?: boolean;

  label?: ReactNode;

  description?: string;
  /**
   * Number input value
   */
  value?: number;
  /**
   * Select value
   */
  selectValue?: string | null;
  /**
   * Select options
   */
  selectData: ComboboxItem[];

  numberPlaceholder?: string;
  /**
   * Placeholder for select field
   */
  selectPlaceholder?: string;
  /**
   * Icon for number field
   */
  numberIcon?: ReactNode;
  /**
   * icon for select field
   */
  selectIcon?: ReactNode;
  /**
   * use indian currency formatting
   * if set takes precedence over thousandsGroupStyle,thousandSeparator
   */
  useCurrencyFormatter?: boolean;
  thousandsGroupStyle?: 'thousand' | 'lakh' | 'wan' | 'none';
  thousandSeparator?: string | boolean;
  /**
   * Enables decimal option in number field with
   * exact precision
   */
  decimalScale?: number;
  disabled?: boolean;
  size?: MantineSize;
  error?: string;
  parser?: string;
  formatter?: string;
  onChange?: (value: number) => void;
  onChangeSelectVal?: (val: string) => void;
}

const NumberSelectGroup = memo(
  forwardRef<HTMLInputElement, NumberSelectGroupProps>(
    (
      {
        size = 'md',
        loading = false,
        label,
        description,
        value,
        selectValue,
        selectData,
        numberPlaceholder,
        selectPlaceholder,
        numberIcon,
        selectIcon,
        disabled,
        useCurrencyFormatter,
        thousandsGroupStyle,
        thousandSeparator,
        decimalScale,
        error,
        onChange,
        onChangeSelectVal,
        ...props
      },
      ref
    ) => {
      const scheme = useMantineColorScheme();
      const [inputFocussed, setInputFocussed] = useState<boolean>(false);
      const [selectFocussed, setSelectFocussed] = useState<boolean>(false);
      const [localSelect, setLocalSelect] = useState<string>(
        selectValue ? selectValue : ''
      );
      const [localValue, setLocalValue] = useState<number | undefined>(value);

      useEffect(() => {
        setLocalValue(value);
      }, [value]);

      useEffect(() => {
        setLocalSelect(selectValue ? selectValue : '');
      }, [selectValue]);

      const rightSectionSelect = loading ? (
        <Loader size={size} />
      ) : error ? (
        <RiErrorWarningLine />
      ) : (
        <RiArrowDownSLine />
      );

      const inputClasses = useMemo(() => {
        return GroupInputOverrideClassNames({
          size,
          focussed: inputFocussed,
          error
        });
      }, [size, inputFocussed, error]);

      const selectClasses = useMemo(() => {
        return GroupSelectOverrideClassNames({
          size,
          focussed: selectFocussed,
          error
        });
      }, [size, selectFocussed, error]);

      const handleChange = (val: number, selectVal: string) => {
        setLocalValue(val);
        setLocalSelect(selectVal);
        onChange?.(val);
        onChangeSelectVal?.(selectVal);
      };

      return (
        <Box lh={1.55} {...props}>
          <Grid gutter={0}>
            {(label || description) && (
              <Grid.Col span={12} mb={4}>
                {label && (
                  <Input.Label
                    fw={500}
                    styles={(theme) => ({
                      label: {
                        color:
                          scheme.colorScheme === 'light'
                            ? theme.colors.text[6]
                            : undefined
                      }
                    })}
                    size={size}
                  >
                    {label}
                  </Input.Label>
                )}
                {description && (
                  <Input.Description size={size}>{description}</Input.Description>
                )}
              </Grid.Col>
            )}
            <Grid.Col span={5} my={2}>
              <NumberInput
                onFocus={() => setInputFocussed(true)}
                onBlur={() => setInputFocussed(false)}
                hideControls
                placeholder={numberPlaceholder}
                leftSection={numberIcon}
                rightSection={undefined}
                size={size}
                error={error ? true : false}
                disabled={disabled}
                decimalScale={decimalScale}
                classNames={{ ...inputClasses }}
                value={localValue || localValue === 0 ? localValue : ''}
                onChange={(e) => handleChange(e ? (e as number) : 0, localSelect)}
                thousandsGroupStyle={
                  useCurrencyFormatter ? 'lakh' : thousandsGroupStyle
                }
                thousandSeparator={useCurrencyFormatter ? ',' : thousandSeparator}
                ref={ref}
              />
            </Grid.Col>
            <Grid.Col span={7} my={2}>
              <Select
                onFocus={() => setSelectFocussed(true)}
                onBlur={() => setSelectFocussed(false)}
                placeholder={selectPlaceholder}
                leftSection={selectIcon}
                data={selectData}
                size={size}
                rightSection={rightSectionSelect}
                error={error ? true : false}
                disabled={disabled}
                value={localSelect}
                onChange={(e) =>
                  e !== null && handleChange(localValue ? localValue : 0, e)
                }
                classNames={{ ...selectClasses }}
              />
            </Grid.Col>
            {error && (
              <Grid.Col span={12} mt={4}>
                <Input.Error c="pink.6" size={size}>
                  {error}
                </Input.Error>
              </Grid.Col>
            )}
          </Grid>
        </Box>
      );
    }
  )
);

export { NumberSelectGroup };
export type { NumberSelectGroupProps };
